import React, { useCallback, useState, useEffect } from "react";
import {
  Box,
  Stack,
  Button,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../AuthContext";
import { ConfirmActionDialog } from "../ConfirmActionDialog";
import {
  useMasterProfileActions,
  ActionPayload,
  MasterProfileActionKey,
  ActionOverrides,
} from "../../hooks/useMasterProfileActions";
import { stripWhitespace } from "../../utils/cleaning";

/** User details for pre-populating the update form */
export type UserDetails = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  dateOfBirth?: string;
};

export type ActionButtonsProps = {
  /** Master Profile ID - required for API calls */
  mpId?: string;
  /** Cognito ID for the user */
  cognitoId?: string;
  /** Zaxbys ID for lock account */
  zaxbysId?: string;
  /** User details for pre-populating forms */
  userDetails?: UserDetails;
  /** Customer name - used for Right to Know report filename */
  customerName?: string;
  /** Whether the account is currently enabled (true) or disabled (false) */
  isAccountEnabled?: boolean;
  /** Whether the user has opted out of data sharing */
  gpcSignal?: boolean;
  /** Whether the account is currently locked */
  isLocked?: boolean;
  /**
   * Optional callback when an action completes successfully.
   * Use this to refresh profile data.
   */
  onActionSuccess?: (actionKey: MasterProfileActionKey) => void;
};

/** Form fields for Update Account */
type UpdateFormFields = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  birthMonth: string;
  birthDay: string;
};

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  mpId,
  cognitoId,
  zaxbysId,
  userDetails,
  customerName,
  isAccountEnabled = true,
  gpcSignal = false,
  isLocked = false,
  onActionSuccess,
}) => {
  const { role } = useAuth();
  const canSeeDnss =
    role === "medley-admin-group" ||
    role === "compliance-group" ||
    role === "guest-experience-manager-group" ||
    role === "guest-experience-group";
  const canSeeRightToKnow =
    role === "guest-experience-manager-group" ||
    role === "compliance-group" ||
    role === "medley-admin-group";
  const canToggleAccountStatus = role !== "compliance-group";

  const buttonStyle = {
    backgroundColor: "#041E42",
    textTransform: "none" as const,
    fontSize: "0.75rem",
  };

  // Form state for Update Account
  const [updateFormFields, setUpdateFormFields] = useState<UpdateFormFields>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    birthMonth: "",
    birthDay: "",
  });

  // Parse birthdate into month and day
  const parseBirthDate = (dateOfBirth?: string): { month: string; day: string } => {
    if (!dateOfBirth) {
      return { month: "", day: "" };
    }
    
    // Handle YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}/.test(dateOfBirth)) {
      const parts = dateOfBirth.split("-");
      return { month: parts[1], day: parts[2].substring(0, 2) };
    }
    
    // Handle MM-DD format
    if (/^\d{2}-\d{2}$/.test(dateOfBirth)) {
      const [month, day] = dateOfBirth.split("-");
      return { month, day };
    }
    
    // Handle MM/DD format
    if (/^\d{2}\/\d{2}$/.test(dateOfBirth)) {
      const [month, day] = dateOfBirth.split("/");
      return { month, day };
    }
    
    return { month: "", day: "" };
  };

  // Reset form fields when userDetails changes or dialog opens
  const resetFormFields = useCallback(() => {
    const { month, day } = parseBirthDate(userDetails?.dateOfBirth);
    setUpdateFormFields({
      firstName: userDetails?.firstName ?? "",
      lastName: userDetails?.lastName ?? "",
      email: userDetails?.email ?? "",
      phoneNumber: userDetails?.phoneNumber ?? "",
      birthMonth: month,
      birthDay: day,
    });
  }, [userDetails]);

  // Initialize form fields when userDetails changes
  useEffect(() => {
    resetFormFields();
  }, [resetFormFields]);

  // Handle form field changes
  const handleFieldChange = (field: keyof UpdateFormFields) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Phone fields never accept whitespace — strip it on every keystroke.
    const nextValue =
      field === "phoneNumber"
        ? stripWhitespace(event.target.value)
        : event.target.value;
    setUpdateFormFields((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  // Construct the payload with user/account data
  const getPayload = useCallback((): ActionPayload => {
    // Combine month and day into MM-DD format for the API
    const dateOfBirth = updateFormFields.birthMonth && updateFormFields.birthDay 
      ? `${updateFormFields.birthMonth}-${updateFormFields.birthDay}`
      : "";
    
    return {
      mpId,
      cognitoId,
      zaxbysId,
      email: userDetails?.email,
      customerName,
      // Current account status - API will toggle to opposite
      isAccountEnabled,
      gpcSignal: true,
      // Include update form fields for updateAccount action
      updateData: {
        firstName: updateFormFields.firstName,
        lastName: updateFormFields.lastName,
        email: updateFormFields.email,
        phoneNumber: updateFormFields.phoneNumber,
        dateOfBirth,
      },
    };
  }, [mpId, cognitoId, zaxbysId, userDetails?.email, customerName, isAccountEnabled, updateFormFields]);

  const {
    dialogOpen,
    currentAction,
    loading,
    errors,
    openDialog,
    closeDialog,
    confirmAction,
    clearErrors,
  } = useMasterProfileActions({
    getPayload,
    onSuccess: (actionKey) => {
      onActionSuccess?.(actionKey);
    },
    onPartialSuccess: (actionKey) => {
      onActionSuccess?.(actionKey);
    },
    onFailure: () => {
      // Handle failure if needed
    },
  });

  // Custom handler to reset form when opening update dialog
  const handleOpenDialog = (actionKey: MasterProfileActionKey, overrides?: ActionOverrides) => {
    if (actionKey === "updateAccount") {
      resetFormFields();
    }
    openDialog(actionKey, overrides);
  };

  // Custom handler to reset form when closing dialog
  const handleCloseDialog = () => {
    resetFormFields();
    closeDialog();
  };

  // Render update account form as dialog description
  const renderUpdateAccountForm = () => (
    <Box sx={{ mt: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={updateFormFields.firstName}
            onChange={handleFieldChange("firstName")}
            disabled={loading}
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={updateFormFields.lastName}
            onChange={handleFieldChange("lastName")}
            disabled={loading}
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={updateFormFields.email}
            onChange={handleFieldChange("email")}
            disabled={loading}
            size="small"
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number"
            value={updateFormFields.phoneNumber}
            onChange={handleFieldChange("phoneNumber")}
            disabled={loading}
            size="small"
            variant="outlined"
            placeholder="(555) 555-5555"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Month</InputLabel>
            <Select
              value={updateFormFields.birthMonth}
              label="Month"
              onChange={(e) => setUpdateFormFields(prev => ({ ...prev, birthMonth: e.target.value }))}
            >
              <MenuItem value="">--</MenuItem>
              <MenuItem value="01">January</MenuItem>
              <MenuItem value="02">February</MenuItem>
              <MenuItem value="03">March</MenuItem>
              <MenuItem value="04">April</MenuItem>
              <MenuItem value="05">May</MenuItem>
              <MenuItem value="06">June</MenuItem>
              <MenuItem value="07">July</MenuItem>
              <MenuItem value="08">August</MenuItem>
              <MenuItem value="09">September</MenuItem>
              <MenuItem value="10">October</MenuItem>
              <MenuItem value="11">November</MenuItem>
              <MenuItem value="12">December</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth size="small" disabled={loading}>
            <InputLabel>Day</InputLabel>
            <Select
              value={updateFormFields.birthDay}
              label="Day"
              onChange={(e) => setUpdateFormFields(prev => ({ ...prev, birthDay: e.target.value }))}
            >
              <MenuItem value="">--</MenuItem>
              {Array.from({ length: 31 }, (_, i) => {
                const day = String(i + 1).padStart(2, "0");
                return <MenuItem key={day} value={day}>{i + 1}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );

  // Get description content based on current action
  const getDialogDescription = () => {
    if (currentAction?.key === "updateAccount") {
      return renderUpdateAccountForm();
    }
    return currentAction?.description ?? "";
  };

  return (
    <>
      <Box
        sx={{ 
          px: 2, 
          py: 1.5, 
          borderTop: 1, 
          borderColor: "divider", 
          backgroundColor: "#f5f5f5",
          mt: "auto" // Push to bottom of flex container
        }}
      >
        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            color="primary"
            sx={{ ...buttonStyle, minWidth: 120 }}
            onClick={() => handleOpenDialog("updateAccount")}
          >
            UPDATE ACCOUNT
          </Button>
          {canToggleAccountStatus && (
            <Button
              variant="contained"
              color="primary"
              sx={{ ...buttonStyle, minWidth: 140 }}
              onClick={() => handleOpenDialog("toggleAccountStatus", {
                label: isAccountEnabled ? "Disable Account" : "Enable Account",
                description: isAccountEnabled 
                  ? "Are you sure you want to disable this account? The user will no longer be able to access their account."
                  : "Are you sure you want to enable this account? The user will be able to access their account again.",
                confirmText: isAccountEnabled ? "Disable" : "Enable",
              })}
            >
              {isAccountEnabled ? "DISABLE ACCOUNT" : "ENABLE ACCOUNT"}
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            sx={{ ...buttonStyle, minWidth: 120 }}
            onClick={() => handleOpenDialog("privacyDelete")}
          >
            PRIVACY DELETE
          </Button>
          {canSeeDnss && (
            <Button
              variant="contained"
              color="primary"
              disabled={gpcSignal}
              sx={{ ...buttonStyle, minWidth: 60 }}
              onClick={() => handleOpenDialog("dnss")}
            >
              DNSS
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            disabled={!isLocked}
            sx={{ ...buttonStyle, minWidth: 120 }}
            onClick={() => handleOpenDialog("lockAccount")}
          >
            UNLOCK ACCOUNT
          </Button>
          {canSeeRightToKnow && (
            <Button
              variant="contained"
              color="primary"
              sx={{ ...buttonStyle, minWidth: 120 }}
              onClick={() => handleOpenDialog("rightToKnow")}
            >
              RIGHT TO KNOW REPORT
            </Button>
          )}
        </Stack>

        {/* Errors Alert (shown after 206 responses) */}
        {errors.length > 0 && (
          <Alert
            severity="warning"
            sx={{ mt: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={clearErrors}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            <AlertTitle>Some Items Failed</AlertTitle>
            <List dense disablePadding>
              {errors.map((error) => (
                <ListItem
                  key={`${error.displayName}-${error.errorMessage ?? ""}`}
                  disablePadding
                  sx={{ pl: 2 }}
                >
                  <ListItemText
                    primary={error.displayName}
                    secondary={error.errorMessage}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{
                      variant: "caption",
                      color: "error",
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Alert>
        )}
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmActionDialog
        open={dialogOpen}
        actionLabel={currentAction?.label ?? ""}
        description={getDialogDescription()}
        confirmText={currentAction?.confirmText}
        loading={loading}
        onConfirm={confirmAction}
        onCancel={handleCloseDialog}
      />
    </>
  );
};
